import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Field, MultilangField } from "@/pages/admin/AdminOffers";

const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const dayDe = {
    monday: "Montag", tuesday: "Dienstag", wednesday: "Mittwoch",
    thursday: "Donnerstag", friday: "Freitag", saturday: "Samstag", sunday: "Sonntag",
};

export default function AdminSettings() {
    const { settings, refresh } = useSettings();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm({
            site_mode: settings.site_mode || "opening_soon",
            opening_date: settings.opening_date || "",
            countdown_enabled: !!settings.countdown_enabled,
            store_name: settings.store_name || "SCHOCK MARKT",
            address: settings.address || "",
            phone: settings.phone || "",
            whatsapp: settings.whatsapp || "",
            email: settings.email || "",
            map_embed_url: settings.map_embed_url || "",
            social_instagram: settings.social_instagram || "",
            social_facebook: settings.social_facebook || "",
            opening_hours: settings.opening_hours || {},
            hero_message: settings.hero_message || { de: "", tr: "", en: "" },
            about_text: settings.about_text || { de: "", tr: "", en: "" },
            seo_title: settings.seo_title || { de: "", tr: "", en: "" },
            seo_description: settings.seo_description || { de: "", tr: "", en: "" },
        });
    }, [settings]);

    if (!form) return null;

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/settings", form);
            await refresh();
            toast.success("Einstellungen gespeichert");
        } catch { toast.error("Fehler beim Speichern"); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={save} className="p-6 md:p-10 max-w-5xl space-y-8" data-testid="settings-form">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-brand-green">Einstellungen</div>
                    <h1 className="mt-1 font-display text-3xl font-bold">Allgemein</h1>
                </div>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save"><Save className="h-4 w-4" />{saving ? "..." : "Speichern"}</button>
            </div>

            <Section title="Site-Modus">
                <div className="grid sm:grid-cols-2 gap-3">
                    <label className={`card-clean p-4 cursor-pointer ${form.site_mode === "opening_soon" ? "ring-2 ring-brand-green" : ""}`}>
                        <input type="radio" name="mode" checked={form.site_mode === "opening_soon"} onChange={() => setForm((f) => ({ ...f, site_mode: "opening_soon" }))} className="hidden" data-testid="mode-opening" />
                        <div className="font-semibold">Eröffnung in Kürze</div>
                        <div className="text-xs text-brand-charcoal/65 mt-1">Pre-Launch Modus mit Eröffnungsankündigung.</div>
                    </label>
                    <label className={`card-clean p-4 cursor-pointer ${form.site_mode === "store_live" ? "ring-2 ring-brand-green" : ""}`}>
                        <input type="radio" name="mode" checked={form.site_mode === "store_live"} onChange={() => setForm((f) => ({ ...f, site_mode: "store_live" }))} className="hidden" data-testid="mode-live" />
                        <div className="font-semibold">Markt geöffnet</div>
                        <div className="text-xs text-brand-charcoal/65 mt-1">Live-Modus: Angebote, Sortiment, Öffnungszeiten im Fokus.</div>
                    </label>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <Field label="Eröffnungsdatum (optional)">
                        <input type="datetime-local" value={form.opening_date?.slice(0, 16) || ""} onChange={(e) => setForm((f) => ({ ...f, opening_date: e.target.value }))} className="input-clean" data-testid="opening-date" />
                    </Field>
                    <Field label="Countdown aktivieren">
                        <label className="inline-flex items-center gap-2 mt-3">
                            <input type="checkbox" checked={form.countdown_enabled} onChange={(e) => setForm((f) => ({ ...f, countdown_enabled: e.target.checked }))} data-testid="countdown-enabled" />
                            <span>Countdown auf der Startseite anzeigen (benötigt Eröffnungsdatum)</span>
                        </label>
                    </Field>
                </div>
            </Section>

            <Section title="Kontakt &amp; Standort">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Marktname"><input value={form.store_name} onChange={(e) => setForm((f) => ({ ...f, store_name: e.target.value }))} className="input-clean" /></Field>
                    <Field label="Adresse"><input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="input-clean" /></Field>
                    <Field label="Telefon"><input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="input-clean" /></Field>
                    <Field label="WhatsApp"><input value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} className="input-clean" /></Field>
                    <Field label="E-Mail"><input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="input-clean" /></Field>
                    <Field label="Google Maps Embed URL"><input value={form.map_embed_url} onChange={(e) => setForm((f) => ({ ...f, map_embed_url: e.target.value }))} className="input-clean" /></Field>
                    <Field label="Instagram URL"><input value={form.social_instagram} onChange={(e) => setForm((f) => ({ ...f, social_instagram: e.target.value }))} className="input-clean" /></Field>
                    <Field label="Facebook URL"><input value={form.social_facebook} onChange={(e) => setForm((f) => ({ ...f, social_facebook: e.target.value }))} className="input-clean" /></Field>
                </div>
            </Section>

            <Section title="Öffnungszeiten">
                <div className="grid sm:grid-cols-2 gap-3">
                    {dayKeys.map((k) => (
                        <Field key={k} label={dayDe[k]}>
                            <input value={form.opening_hours[k] || ""} onChange={(e) => setForm((f) => ({ ...f, opening_hours: { ...f.opening_hours, [k]: e.target.value } }))} className="input-clean" placeholder="08:00 – 20:00 oder geschlossen" />
                        </Field>
                    ))}
                </div>
            </Section>

            <Section title="Hero-Botschaft (mehrsprachig)">
                <MultilangField label="Hero-Untertitel" value={form.hero_message} onChange={(v) => setForm((f) => ({ ...f, hero_message: v }))} multiline />
            </Section>

            <Section title="Über-Text (mehrsprachig)">
                <MultilangField label="Über-Text" value={form.about_text} onChange={(v) => setForm((f) => ({ ...f, about_text: v }))} multiline />
            </Section>

            <Section title="SEO (mehrsprachig)">
                <MultilangField label="Meta-Title" value={form.seo_title} onChange={(v) => setForm((f) => ({ ...f, seo_title: v }))} />
                <MultilangField label="Meta-Description" value={form.seo_description} onChange={(v) => setForm((f) => ({ ...f, seo_description: v }))} multiline />
            </Section>
        </form>
    );
}

function Section({ title, children }) {
    return (
        <section className="card-clean p-6 md:p-8">
            <h2 className="font-display text-xl font-semibold">{title}</h2>
            <div className="mt-5">{children}</div>
        </section>
    );
}
